/**
 * BIE Pre-Abandonment Intervention Tracker
 * Inject into Shopify theme.liquid before </body>
 * Runs only on product pages (/products/) and cart page (/cart)
 * Scores behavioural signals, fires Claude-powered intervention widget
 */

(function () {
  'use strict';

  // ─── CONFIG ──────────────────────────────────────────────────────────────
  const CONFIG = {
    apiEndpoint: 'https://bie-prospecting-bot-production.up.railway.app/api/intervention',
    triggerThreshold: 50,
    minTimeOnPage: 5000,
    cooldownPeriod: 120000,
    highValueThreshold: 200,
    activePages: ['product', 'cart'],
    whatsappNumber: '447822032344',
    signals: {
      mouseVelocityExit: 30,
      rapidScrollUp: 20,
      tabHidden: 35,
      cartDwellLong: 15,
      formHesitation: 15,
      repeatedProductHover: 10,
      backButtonHint: 40,
    }
  };

  // ─── STATE ────────────────────────────────────────────────────────────────
  const state = {
    score: 0,
    pageType: null,
    pageLoadTime: Date.now(),
    lastInterventionTime: 0,
    hasTriggered: false,
    mouseHistory: [],
    lastScrollY: window.scrollY,
    lastScrollTime: Date.now(),
    formFieldFocusTime: null,
    productHoverCounts: {},
    cartData: null,
    interventionShown: false,
  };

  // ─── PAGE DETECTION ───────────────────────────────────────────────────────
  function detectPageType() {
    try {
      const path = window.location.pathname || '';
      if (path.includes('/products/')) return 'product';
      if (path === '/cart') return 'cart';
      return null;
    } catch(e) {
      return null;
    }
  }

  state.pageType = detectPageType();

  if (!state.pageType || !CONFIG.activePages.includes(state.pageType)) return;

  // ─── WHATSAPP LINK BUILDER ────────────────────────────────────────────────
  function buildWhatsAppUrl(pageType, productTitle) {
    var message;
    if (pageType === 'cart') {
      message = 'Hi Hayley, I was just looking at my cart on the Brand It Essex website and had a quick question before ordering.';
    } else if (productTitle) {
      message = 'Hi Hayley, I was looking at ' + productTitle + ' on the Brand It Essex website and had a question about it.';
    } else {
      message = 'Hi Hayley, I was just browsing the Brand It Essex website and wanted to ask a quick question about your workwear.';
    }
    return 'https://wa.me/' + CONFIG.whatsappNumber + '?text=' + encodeURIComponent(message);
  }

  // ─── CART DATA EXTRACTION ─────────────────────────────────────────────────
  async function fetchCartData() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      state.cartData = cart;
    } catch (e) {}
  }

  function getProductData() {
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      const meta = window.ShopifyAnalytics.meta;
      return {
        title: meta.product ? meta.product.title : null,
        type: meta.product ? meta.product.type : null,
        vendor: meta.product ? meta.product.vendor : null,
        price: meta.product ? meta.product.price / 100 : null,
      };
    }
    return { title: document.title.replace(' \u2013 Brand It Essex', '').trim() };
  }

  // ─── SCORE ENGINE ─────────────────────────────────────────────────────────
  function addScore(signal, value) {
    if (state.hasTriggered) return;
    state.score = Math.min(100, state.score + value);
    console.debug('[BIE] Signal: ' + signal + ' +' + value + ' | Total: ' + state.score);
    checkThreshold();
  }

  function checkThreshold() {
    if (state.hasTriggered || state.interventionShown) return;
    const timeOnPage = Date.now() - state.pageLoadTime;
    if (timeOnPage < CONFIG.minTimeOnPage) return;
    if ((Date.now() - state.lastInterventionTime) <= CONFIG.cooldownPeriod) return;
    if (state.score >= CONFIG.triggerThreshold) triggerIntervention();
  }

  // ─── SIGNAL 1: CLOSE BUTTON EXIT INTENT ──────────────────────────────────
  // Fires only when cursor moves fast toward top-right corner (browser close button zone)
  // Requires: top 8% of viewport height AND right 15% of viewport width
  document.addEventListener('mousemove', function (e) {
    const now = Date.now();
    state.mouseHistory.push({ x: e.clientX, y: e.clientY, t: now });
    if (state.mouseHistory.length > 10) state.mouseHistory.shift();
    if (state.mouseHistory.length < 3) return;
    const oldest = state.mouseHistory[0];
    const newest = state.mouseHistory[state.mouseHistory.length - 1];
    const timeDelta = newest.t - oldest.t;
    if (timeDelta === 0) return;
    const distY = oldest.y - newest.y;
    const velocity = distY / timeDelta;
    // Must be moving upward fast, into the top 8% of screen, AND in the right 15% (close button zone)
    if (
      velocity > 0.8 &&
      newest.y < window.innerHeight * 0.08 &&
      newest.x > window.innerWidth * 0.85
    ) {
      addScore('mouseVelocityExit', CONFIG.signals.mouseVelocityExit);
    }
  }, { passive: true });

  // ─── SIGNAL 2: RAPID UPWARD SCROLL ───────────────────────────────────────
  document.addEventListener('scroll', function () {
    const now = Date.now();
    const currentY = window.scrollY;
    const timeDelta = now - state.lastScrollTime;
    const scrollDelta = state.lastScrollY - currentY;
    if (timeDelta > 0) {
      const scrollVelocity = scrollDelta / timeDelta;
      if (scrollVelocity > 1.5 && scrollDelta > 100) {
        addScore('rapidScrollUp', CONFIG.signals.rapidScrollUp);
      }
    }
    state.lastScrollY = currentY;
    state.lastScrollTime = now;
  }, { passive: true });

  // ─── SIGNAL 3: TAB SWITCHING ──────────────────────────────────────────────
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) addScore('tabHidden', CONFIG.signals.tabHidden);
  });

  // ─── SIGNAL 4: CART PAGE DWELL ───────────────────────────────────────────
  if (state.pageType === 'cart') {
    setTimeout(function () {
      if (!state.hasTriggered && !state.interventionShown) {
        addScore('cartDwellLong', CONFIG.signals.cartDwellLong);
      }
    }, 90000);
    fetchCartData();
  }

  // ─── CART 60-SECOND DIRECT TRIGGER ─────────────────────────────────────────
  // Fires regardless of score after 60s on cart if not yet triggered
  if (state.pageType === 'cart') {
    setTimeout(function () {
      if (state.hasTriggered || state.interventionShown) return;
      var cartWaUrl = buildWhatsAppUrl('cart', null);
      var msg = 'Still sorting your order? If you want to check anything before you checkout I am happy to help. Just send me a message.';
      state.hasTriggered = true;
      state.interventionShown = true;
      state.lastInterventionTime = Date.now();
      showInterventionWidget(msg, cartWaUrl, false, false, 'Ready when you are');
    }, 60000);
  }

  // ─── CART 60-SECOND DIRECT TRIGGER ─────────────────────────────────────────
  // Fires silently after 60s on cart if nothing else has triggered yet
  if (state.pageType === 'cart') {
    setTimeout(function () {
      if (state.hasTriggered || state.interventionShown) return;
      var cartWaUrl = buildWhatsAppUrl('cart', null);
      var msg = 'Still sorting your order? If you want to check anything before you checkout I am happy to help. Just send me a message.';
      state.hasTriggered = true;
      state.interventionShown = true;
      state.lastInterventionTime = Date.now();
      showInterventionWidget(msg, cartWaUrl, false, false, 'Ready when you are');
    }, 60000);
  }

  // ─── SIGNAL 5: FORM HESITATION ───────────────────────────────────────────
  document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"]').forEach(function (input) {
    input.addEventListener('focus', function () {
      state.formFieldFocusTime = Date.now();
    });
    input.addEventListener('blur', function () {
      if (!state.formFieldFocusTime) return;
      const dwellTime = Date.now() - state.formFieldFocusTime;
      if (dwellTime < 3000 && this.value.length < 3) {
        addScore('formHesitation', CONFIG.signals.formHesitation);
      }
      state.formFieldFocusTime = null;
    });
  });

  // ─── SIGNAL 6: REPEATED PRODUCT ELEMENT HOVER ────────────────────────────
  if (state.pageType === 'product') {
    const trackableElements = document.querySelectorAll(
      '.product__title, .product__price, .product-form__submit, .product__description'
    );
    trackableElements.forEach(function (el) {
      const key = el.className || el.tagName;
      state.productHoverCounts[key] = 0;
      el.addEventListener('mouseenter', function () {
        state.productHoverCounts[key] = (state.productHoverCounts[key] || 0) + 1;
        if (state.productHoverCounts[key] >= 3) {
          addScore('repeatedProductHover', CONFIG.signals.repeatedProductHover);
          state.productHoverCounts[key] = 0;
        }
      });
    });
  }

  // ─── SIGNAL 7: BACK BUTTON HINT ──────────────────────────────────────────
  history.pushState(null, null, window.location.href);
  window.addEventListener('popstate', function () {
    addScore('backButtonHint', CONFIG.signals.backButtonHint);
    history.pushState(null, null, window.location.href);
  });

  // ─── TRIGGER INTERVENTION ────────────────────────────────────────────────
  async function triggerIntervention() {
    state.hasTriggered = true;
    state.lastInterventionTime = Date.now();

    const payload = buildPayload();
    const productData = state.pageType === 'product' ? getProductData() : null;
    const productTitle = productData ? productData.title : null;
    const waUrl = buildWhatsAppUrl(state.pageType, productTitle);

    try {
      const res = await fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      showInterventionWidget(data.message, waUrl, payload.isHighValue);
      state.interventionShown = true;
    } catch (err) {
      const fallback = state.pageType === 'cart'
        ? 'Need help with your order? Unsure on embroidery costs? Send me a message and I\'ll walk you through it.'
        : 'Need help finding the perfect product? Unsure on embroidery costs? Send me a message and I\'ll walk you through it.';
      showInterventionWidget(fallback, waUrl, false);
      state.interventionShown = true;
    }
  }

  function buildPayload() {
    const cartValue = state.cartData ? (state.cartData.total_price / 100) : null;
    const cartItems = state.cartData
      ? state.cartData.items.map(i => ({ title: i.title, quantity: i.quantity, price: i.price / 100 }))
      : [];
    const productData = state.pageType === 'product' ? getProductData() : null;
    return {
      pageType: state.pageType,
      cartValue,
      cartItems,
      productData,
      score: state.score,
      isHighValue: cartValue ? cartValue >= CONFIG.highValueThreshold : false,
      timeOnPage: Math.round((Date.now() - state.pageLoadTime) / 1000),
    };
  }

  // ─── INTERVENTION WIDGET ──────────────────────────────────────────────────
  function showInterventionWidget(message, waUrl, isHighValue, playSound, senderSubOverride) {
    if (playSound === undefined) playSound = true;
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bie-in {
        from { transform: translateX(calc(100% + 32px)); opacity: 0; }
        to   { transform: translateX(0); opacity: 1; }
      }
      @keyframes bie-out {
        from { transform: translateX(0); opacity: 1; }
        to   { transform: translateX(calc(100% + 32px)); opacity: 0; }
      }
      @keyframes bie-in-mob {
        from { transform: translateY(100%); }
        to   { transform: translateY(0); }
      }
      @keyframes bie-out-mob {
        from { transform: translateY(0); }
        to   { transform: translateY(100%); }
      }
      #bie-widget {
        position: fixed;
        bottom: 24px;
        right: 24px;
        width: 360px;
        max-width: calc(100vw - 32px);
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 8px 30px rgba(10,28,64,0.2), 0 2px 8px rgba(10,28,64,0.1);
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        overflow: hidden;
        animation: bie-in 0.42s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        border: 1px solid rgba(10,28,64,0.1);
      }
      #bie-widget.bie-out {
        animation: bie-out 0.32s cubic-bezier(0.4, 0, 1, 1) forwards;
      }
      #bie-widget .bie-header {
        background: #0d2152;
        padding: 14px 16px 13px;
        display: flex;
        align-items: center;
        gap: 10px;
        position: relative;
      }
      #bie-widget .bie-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #1a4db8;
        border: 2px solid rgba(255,255,255,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        color: #ffffff;
        letter-spacing: 0.04em;
        flex-shrink: 0;
      }
      #bie-widget .bie-sender-name {
        font-size: 13px;
        font-weight: 600;
        color: #ffffff;
        line-height: 1.2;
      }
      #bie-widget .bie-sender-sub {
        font-size: 11px;
        color: rgba(255,255,255,0.5);
        margin-top: 2px;
      }
      #bie-widget .bie-close {
        position: absolute;
        top: 10px;
        right: 12px;
        background: none;
        border: none;
        color: rgba(255,255,255,0.4);
        font-size: 20px;
        line-height: 1;
        cursor: pointer;
        padding: 0;
        transition: color 0.15s;
      }
      #bie-widget .bie-close:hover { color: #ffffff; }
      #bie-widget .bie-body {
        padding: 16px 18px 18px;
      }
      #bie-widget .bie-msg {
        font-size: 14.5px;
        line-height: 1.6;
        color: #1a1a2e;
        margin-bottom: 14px;
      }
      #bie-widget .bie-cta {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        box-sizing: border-box;
        background: #25D366;
        color: #ffffff;
        text-decoration: none;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.02em;
        padding: 11px 16px;
        border-radius: 5px;
        transition: background 0.18s, transform 0.1s;
      }
      #bie-widget .bie-cta:hover { background: #20b858; transform: translateY(-1px); }
      #bie-widget .bie-cta:active { transform: translateY(0); }
      #bie-widget .bie-note {
        font-size: 11px;
        color: #8a93a8;
        text-align: center;
        margin-top: 9px;
      }
      @media (max-width: 480px) {
        #bie-widget {
          bottom: 0; right: 0; left: 0;
          width: 100%; max-width: 100%;
          border-radius: 12px 12px 0 0;
          animation: bie-in-mob 0.38s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        #bie-widget.bie-out {
          animation: bie-out-mob 0.3s cubic-bezier(0.4, 0, 1, 1) forwards;
        }
      }
    `;
    document.head.appendChild(style);

    const widget = document.createElement('div');
    widget.id = 'bie-widget';
    widget.setAttribute('role', 'dialog');
    widget.setAttribute('aria-label', 'Message from Brand It Essex');

    var senderSub = senderSubOverride || (isHighValue ? 'Priority support' : 'Brand It Essex');

    widget.innerHTML = `
      <div class="bie-header">
        <div class="bie-avatar">BIE</div>
        <div>
          <div class="bie-sender-name">Hayley</div>
          <div class="bie-sender-sub">${escapeHtml(senderSub)}</div>
        </div>
        <button class="bie-close" id="bie-close-btn" aria-label="Close">&times;</button>
      </div>
      <div class="bie-body">
        <div class="bie-msg">${escapeHtml(message)}</div>
        <a class="bie-cta" href="${escapeHtml(waUrl)}" target="_blank" rel="noopener">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Chat with us on WhatsApp
        </a>
        <div class="bie-note">Usually replies within minutes</div>
      </div>
    `;

    document.body.appendChild(widget);

    // Play a soft notification chime
    if (playSound) try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, ctx.currentTime);
      osc1.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.08);
      osc1.connect(gain);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.6);
      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1320, ctx.currentTime + 0.1);
      osc2.frequency.exponentialRampToValueAtTime(1100, ctx.currentTime + 0.25);
      const gain2 = ctx.createGain();
      gain2.connect(ctx.destination);
      gain2.gain.setValueAtTime(0, ctx.currentTime + 0.1);
      gain2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
      osc2.connect(gain2);
      osc2.start(ctx.currentTime + 0.1);
      osc2.stop(ctx.currentTime + 0.7);
    } catch(e) {}

    function closeWidget() {
      widget.classList.add('bie-out');
      setTimeout(function () {
        if (document.body.contains(widget)) widget.remove();
      }, 340);
    }

    document.getElementById('bie-close-btn').addEventListener('click', closeWidget);

    setTimeout(function () {
      if (document.body.contains(widget)) closeWidget();
    }, 25000);
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── STICKY CART BAR ─────────────────────────────────────────────────────
  // Shows on product and collection pages when cart has items
  // Hidden on /cart and /checkouts/
  function initStickyCartBar() {
    var path = window.location.pathname || '';
    if (path === '/cart' || path.includes('/checkouts/')) return;

    fetch('/cart.js')
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        if (!cart || !cart.item_count || cart.item_count < 1) return;

        var count = cart.item_count;
        var label = count === 1 ? '1 item' : count + ' items';

        var style = document.createElement('style');
        style.textContent = [
          '#bie-sticky-bar {',
          '  position: fixed;',
          '  bottom: 0; left: 0; right: 0;',
          '  background: #0d2152;',
          '  padding: 11px 20px;',
          '  display: flex;',
          '  align-items: center;',
          '  justify-content: space-between;',
          '  z-index: 999998;',
          '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;',
          '  transform: translateY(100%);',
          '  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);',
          '  gap: 12px;',
          '}',
          '#bie-sticky-bar.bie-bar-visible {',
          '  transform: translateY(0);',
          '}',
          '#bie-sticky-bar .bie-bar-text {',
          '  font-size: 13px;',
          '  color: rgba(255,255,255,0.85);',
          '  display: flex;',
          '  align-items: center;',
          '  gap: 9px;',
          '}',
          '#bie-sticky-bar .bie-bar-count {',
          '  color: #ffffff;',
          '  font-weight: 600;',
          '}',
          '#bie-sticky-bar .bie-bar-cta {',
          '  background: #ffffff;',
          '  color: #0d2152;',
          '  font-size: 12px;',
          '  font-weight: 600;',
          '  padding: 7px 16px;',
          '  border-radius: 4px;',
          '  text-decoration: none;',
          '  letter-spacing: 0.02em;',
          '  white-space: nowrap;',
          '  flex-shrink: 0;',
          '  transition: background 0.15s;',
          '}',
          '#bie-sticky-bar .bie-bar-cta:hover {',
          '  background: #e8edf5;',
          '}',
          '@media (max-width: 480px) {',
          '  #bie-sticky-bar { padding: 11px 16px; }',
          '  #bie-sticky-bar .bie-bar-text { font-size: 12px; }',
          '}',
        ].join('');
        document.head.appendChild(style);

        var bar = document.createElement('div');
        bar.id = 'bie-sticky-bar';
        bar.setAttribute('role', 'complementary');
        bar.setAttribute('aria-label', 'Cart reminder');
        bar.innerHTML = [
          '<div class="bie-bar-text">',
          '  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.65)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
          '    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>',
          '    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>',
          '  </svg>',
          '  You have <span class="bie-bar-count">' + label + '</span> in your basket',
          '</div>',
          '<a class="bie-bar-cta" href="/cart">Complete order</a>',
        ].join('');

        document.body.appendChild(bar);

        // Slide in after short delay
        requestAnimationFrame(function() {
          setTimeout(function() {
            bar.classList.add('bie-bar-visible');
          }, 800);
        });

        // Hide bar if intervention widget appears (avoid overlap)
        var observer = new MutationObserver(function() {
          if (document.getElementById('bie-widget')) {
            bar.style.bottom = '0';
          }
        });
        observer.observe(document.body, { childList: true });
      })
      .catch(function() {});
  }

  // initStickyCartBar(); // DISABLED

  // ─── INIT ─────────────────────────────────────────────────────────────────
  if (state.pageType === 'product') fetchCartData();

  console.debug('[BIE] Behaviour tracker active on:', state.pageType, 'page');

})();